import { Link, data } from 'react-router';
import type { Route } from './+types/industries.$slug';
import { buildMeta, breadcrumbSchema, faqSchema } from '~/lib/seo';
import { industryBySlug, shippedIndustries } from '~/data/industries';
import { SITE } from '~/data/site';

export function loader({ params }: Route.LoaderArgs) {
  const industry = industryBySlug(params.slug);
  if (!industry) throw data('Not found', { status: 404 });
  return { industry };
}

export function meta({ data: loaded }: Route.MetaArgs) {
  if (!loaded) return [{ title: 'Not found' }];
  const { industry } = loaded;
  return buildMeta({
    title: `Cleaning for ${industry.name.toLowerCase()} in Tampa Bay`,
    description: `${industry.blurb} ${industry.headline}.`,
    path: `/industries/${industry.slug}`,
    jsonLd: [
      breadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Industries', url: '/industries' },
        { name: industry.name, url: `/industries/${industry.slug}` },
      ]),
      // Only the answers that actually render — structured data must never assert
      // something the page itself does not say.
      faqSchema(industry.answers.filter((a) => !a.gate || SITE.claims[a.gate])),
    ],
  });
}

export default function IndustryDetail({ loaderData }: Route.ComponentProps) {
  const { industry } = loaderData;
  const others = shippedIndustries().filter((i) => i.slug !== industry.slug);

  // A gated answer is one whose underlying fact is not true yet. It is omitted
  // entirely rather than softened — a hedged version of a credential still reads as
  // a claim, and the whole point is not to make one.
  const answers = industry.answers.filter((a) => !a.gate || SITE.claims[a.gate]);
  const docs = (industry.documentation ?? []).filter((d) => !d.gate || SITE.claims[d.gate]);

  return (
    <>
      <section className="hero dusk grain">
        <div className="shell">
          <div className="hero-inner">
            <p className="eyebrow-pill">{industry.name}</p>
            <h1>{industry.headline}</h1>
            <p className="lede">{industry.intro}</p>
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Get a quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="night-band">
        <div className="shell">
          <div className="stack" style={{ maxWidth: '46ch' }}>
            <p className="eyebrow">The thing you are actually worried about</p>
            <h2 style={{ fontSize: 'var(--step-2)' }}>&ldquo;{industry.fear}&rdquo;</h2>
            <p>Fair. Here are the answers, in writing, before you have to ask for them.</p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="split">
            <div className="stack">
              <p className="eyebrow">Questions we get</p>
              <div className="faq">
                {answers.map((qa) => (
                  <details key={qa.q} open>
                    <summary>{qa.q}</summary>
                    <p>{qa.a}</p>
                  </details>
                ))}
              </div>
            </div>

            <div className="stack split-sticky">
              {docs.length > 0 && (
                <div className="callout">
                  <p className="eyebrow">Paperwork we keep current</p>
                  <ul className="dash">
                    {docs.map((d) => (
                      <li key={d.text}>{d.text}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="stack-sm">
                <h4>Other buildings we clean</h4>
                <ul className="dash">
                  {others.map((i) => (
                    <li key={i.slug}>
                      <Link to={`/industries/${i.slug}`}>{i.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="dawn">
        <div className="shell">
          <div className="cta-inner">
            <h2>Twenty minutes and you get a real number.</h2>
            <p>We walk the space, count what is there, and put a fixed price in writing.</p>
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Book a walkthrough
              </Link>
              <Link to="/pricing" className="btn btn-secondary">
                See pricing first
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
